/**
 * Figure 5, step 2: "Enter your code."
 *
 * Three details on this screen are load-bearing and none of them is visual.
 *
 * **The remaining attempts are shown before the last one, not after it.** `challengeIssuedSchema`
 * carries `attemptsRemaining` for exactly this: too many wrong codes stops the person trying, and
 * discovering that on the last attempt is the surprise this exists to prevent. Since D-25 what it
 * costs is a wait rather than a new link from the rep - the limit became a window, and the remedy
 * moved with it.
 *
 * **Nothing here can be told after the fact, which is why DG.11's warning arrives before it.** A
 * second code overwrites the first in place (`replaceChallengeCode`), so the older one fails exactly
 * as a typo does and the server has no way to say which happened - the digest it would need to
 * compare against no longer exists. The line under the resend button is the only moment the
 * distinction can be drawn at all, and it is drawn before somebody spends a code finding out.
 *
 * **The input never echoes into an error message.** A 400 that quotes the submitted code puts that
 * code in a browser's network log, which is why `guest.controller.ts` refuses to do it either.
 */
import { useState } from 'react';
import { Button, Card, Field, Notice, Page } from '@iqz-ddds/components';
import { FailureNotice } from '../components/FailureNotice';
import { CHALLENGE_CODE_PATTERN } from '@iqz-ddds/contracts';

export interface CodeScreenProps {
  /** Masked by the server on the link path; on the sign-in path it is only what the person typed. */
  readonly maskedEmail: string;
  /**
   * When the code stops working, or null where nothing was said.
   *
   * Two paths and two answers, which is why this is nullable rather than a string with a sentinel.
   * The link path gets a real `expiresAt` from `challengeIssuedSchema`. The sign-in path is answered
   * 202 with an empty body, deliberately, so there is nothing to render and `App.tsx` used to invent
   * `''` to satisfy the type - a value that meant "no deadline" while claiming to be one.
   */
  readonly expiresAt: string | null;
  /** Null on the sign-in path, for the same reason. Absent is not the same as five. */
  readonly attemptsRemaining: number | null;
  /** Empty on the sign-in path, which belongs to no one representative. See the footer. */
  readonly repName: string;
  /**
   * Whether we actually know a code went to that address. False on the sign-in path.
   *
   * `POST /guest/v1/sign-in` answers the same way for an address we hold and one we do
   * not, because differing would publish the firm's client list. So on that path the
   * surface knows only what the person typed, and the hint has to say "if we have" rather
   * than "sent to" - the second would be a disclosure the endpoint refused to make, and a
   * lie to anybody who mistyped their own address.
   *
   * Defaults to true, because the link path *does* know: a live grant carries the masked
   * address and the code was sent in response to it.
   */
  readonly addressConfirmed?: boolean;
  readonly onVerify: (input: { code: string }) => Promise<void>;
  /** Ask for another. A code is short-lived and mail is not instant. Cancels the current one. */
  readonly onResend: () => Promise<void>;
}

/**
 * The deadline as a wall clock, or null where there is nothing honest to show.
 *
 * A time rather than a countdown, and that is the DG.11 decision rather than a shortcut. "Ten
 * minutes" was measured from an issue the reader did not witness, so it was already wrong by however
 * long the mail took and however long they took to open it - a validation session missed the window
 * by forty seconds against that sentence. A ticking counter would fix the arithmetic and cost a
 * re-render a second, plus the browser-clock problem `widget/src/embed/useHostSession.ts` already
 * records; a wall clock costs neither and is read against the one beside it on the same screen.
 */
function deadlineTime(iso: string | null): string | null {
  if (iso === null) return null;

  const at = new Date(iso);
  // `Invalid Date` formats as the literal string, which is worse than saying nothing at all.
  return Number.isNaN(at.getTime()) ? null : at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function CodeScreen({ maskedEmail, expiresAt, attemptsRemaining, repName, addressConfirmed = true, onVerify, onResend }: CodeScreenProps): React.JSX.Element {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);

  const deadline = deadlineTime(expiresAt);
  // The sentence rather than the value, so the hint below stays one flat template rather than a
  // ternary and a second template nested inside one.
  const expirySentence = deadline === null ? '' : ` It expires at ${deadline}.`;

  const submit = async (): Promise<void> => {
    // Client-side only to save a round trip on an obvious typo. The server validates regardless, and
    // its refusal is the one that counts.
    if (!CHALLENGE_CODE_PATTERN.test(code)) {
      setFieldError('A code is six digits.');
      return;
    }

    setBusy(true);
    setError(null);
    setFieldError(undefined);
    try {
      await onVerify({ code });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Something went wrong.'));
      // Cleared on failure, because the next attempt is a fresh six digits and a partially edited
      // wrong code is the easiest way to spend another of five attempts.
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page eyebrow="Secure documents" title="Enter your code">
      <FailureNotice error={error} />

      {/*
        * Warned before the last attempt rather than after it. Below three, the number is the most
        * useful thing on the screen.
        */}
      {attemptsRemaining !== null && attemptsRemaining <= 2 && attemptsRemaining > 0 && (
        <Notice>
          {attemptsRemaining === 1
            ? // Not "a new link from your adviser" any more. D-25 made the limit a window, so the
              // remedy is time. It must not say "ask for a new code" either: a resend zeroes the
              // row's attempts while the per-contact limiter that actually refuses keeps counting,
              // so that advice would send somebody round a loop that cannot succeed yet.
              'One attempt left. After that you will need to wait before trying again.'
            : `${attemptsRemaining} attempts left.`}
        </Notice>
      )}

      <Card>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <Field
            id="code"
            label="Six-digit code"
            /*
              Three sentences rather than two, because there are three states of knowledge.

              The deadline is stated only where the server gave us one. The old copy said "ten
              minutes" on both paths, which was a guess in two directions at once: the sign-in
              endpoint had told us nothing, and `challengeTtlMinutes` is configurable to sixty, so
              the number was wrong for any deployment that had changed it. The email interpolates
              the configured value and is the thing they are already opening to get the code, so it
              is the better place for it when we cannot be exact here.
            */
            hint={
              addressConfirmed
                ? `Sent to ${maskedEmail}.${expirySentence}`
                : `If we have documents for ${maskedEmail}, a six-digit code is on its way. The email says when it expires.`
            }
            value={code}
            onChange={(value) => {
              // Digits only, so a pasted "123 456" or an autofilled "Code: 123456" still works.
              setCode(value.replaceAll(/\D/g, '').slice(0, 6));
              setFieldError(undefined);
            }}
            appearance="code"
            inputMode="numeric"
            // Lets iOS and Android offer the code straight from the notification, which is the single
            // biggest difference to how this feels on a phone.
            autoComplete="one-time-code"
            maxLength={6}
            disabled={busy}
            {...(fieldError === undefined ? {} : { error: fieldError })}
          />

          <Button type="submit" disabled={busy || code.length < 6}>
            {busy ? 'Checking…' : 'Continue'}
          </Button>
        </form>
      </Card>

      {/*
        * DG.11. The warning belongs here, at the button, and not in the hint above.
        *
        * A second code overwrites the first, so the email somebody scrolls back to is dead the
        * moment they press this - and the refusal they then get is "That code does not match",
        * indistinguishable from a typo because the server no longer holds the digest that would
        * tell them apart. Nothing downstream can explain it, so this is the last point at which it
        * can be said at all. Beside the control rather than three paragraphs above it, because a
        * caution read before the decision is a caution nobody is making yet.
        *
        * "Use the newest email" is the same instruction the 401 gives ("Check the most recent
        * email"), so the screen and the failure agree rather than each inventing their own wording.
        */}
      <Button variant="quiet" size="small" onClick={() => void onResend()} disabled={busy}>
        Send another code
      </Button>
      <p className="ddds-row__meta">Asking for another code cancels the one you have. Use the newest email.</p>

      {/*
        * Named where there is a name to use, and general where there is not.
        *
        * The sign-in path has no representative - a contact may be dealing with several, and
        * picking one would be inventing a fact. Interpolating an empty string produced the
        * sentence " will see that you opened this", which is how this was found: in a browser,
        * because no unit test reads a sentence for whether it still parses.
        */}
      <p className="ddds-footer">
        {repName === ''
          ? 'Your adviser will see that you opened this. They will not see the code.'
          : `${repName} will see that you opened this. They will not see the code.`}
      </p>
    </Page>
  );
}
